/**
 * BadTrackEraser
 * --------------
 * Admin Panel → Song Identity tab → "Διαγραφή Λάθος Τραγουδιού"
 *
 * Ροή:
 *   1. Ψάχνεις artist/title (π.χ. "David Blasucci")
 *   2. Πατάς "Λάθος αυτό" σε ένα row → φορτώνει αυτόματα τον winner
 *   3. Επιβεβαιώνεις/αλλάζεις τον winner
 *   4. Πατάς "Διαγραφή & Συγχώνευση" → όλα γίνονται με 1 κλικ
 *
 * Όλα τα κείμενα στα Ελληνικά. Αναστρέψιμη ενέργεια (κουμπί "Επαναφορά").
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import storage from '../utils/storage';
import { getBackendUrl } from '../utils/api';
import {
  Search, Loader2, AlertTriangle, CheckCircle2,
  Trash2, Undo2, ArrowRight, ShieldAlert, RefreshCw,
  Layers,
} from 'lucide-react';

const API = getBackendUrl();

const BadTrackEraser = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const [bad, setBad] = useState(null);
  const [winner, setWinner] = useState(null);
  const [winnerSearchQuery, setWinnerSearchQuery] = useState('');
  const [winnerSearchResults, setWinnerSearchResults] = useState([]);
  const [winnerSearching, setWinnerSearching] = useState(false);
  const [loadingSuggest, setLoadingSuggest] = useState(false);

  const [erasing, setErasing] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);

  // Multi-select state — bulk merge into ONE winner
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkWinner, setBulkWinner] = useState(null);
  const [bulkWinnerQuery, setBulkWinnerQuery] = useState('');
  const [bulkWinnerResults, setBulkWinnerResults] = useState([]);
  const [bulkWinnerSearching, setBulkWinnerSearching] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const auth = useCallback(() => ({
    headers: { Authorization: `Bearer ${storage.getItem('token')}` },
  }), []);

  // Load summary
  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/admin/bad-track/blacklist-summary`, auth());
      setSummary(r.data);
    } catch (_e) { /* ignore */ }
    try {
      const p = await axios.get(`${API}/api/admin/bad-track/progress`, auth());
      setProgress(p.data);
    } catch (_e) { /* ignore */ }
  }, [auth]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  const search = async () => {
    if (!query || query.trim().length < 2) {
      setError('Δώσε τουλάχιστον 2 χαρακτήρες');
      return;
    }
    setSearching(true);
    setError(null);
    setResults([]);
    setBulkSelected(new Set());  // reset bulk selection on new search
    try {
      const r = await axios.get(`${API}/api/admin/bad-track/search`, {
        ...auth(),
        params: { q: query.trim(), limit: 50 },
      });
      setResults(r.data.results || []);
      if ((r.data.results || []).length === 0) {
        setError('Δεν βρέθηκε κανένα τραγούδι');
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSearching(false);
    }
  };

  // ─── Bulk merge helpers ──────────────────────────────────────────
  const toggleBulkRow = (id) => {
    setBulkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleBulkAll = () => {
    const eligible = results
      .filter((r) => !r.is_blacklisted)
      .map((r) => r.id);
    setBulkSelected((prev) => {
      if (prev.size === eligible.length) return new Set();
      return new Set(eligible);
    });
  };

  const searchBulkWinners = async (q) => {
    if (!q || q.trim().length < 2) {
      setBulkWinnerResults([]);
      return;
    }
    setBulkWinnerSearching(true);
    try {
      const r = await axios.get(`${API}/api/admin/bad-track/search`, {
        ...auth(),
        params: { q: q.trim(), limit: 30 },
      });
      const list = (r.data.results || []).filter(
        (x) => !x.is_merged && !x.is_blacklisted && !bulkSelected.has(x.id)
      );
      setBulkWinnerResults(list);
    } catch {
      setBulkWinnerResults([]);
    } finally {
      setBulkWinnerSearching(false);
    }
  };

  const runBulkMerge = async () => {
    if (!bulkWinner || bulkSelected.size === 0) return;
    const ids = Array.from(bulkSelected).filter((id) => id !== bulkWinner.id);
    if (ids.length === 0) {
      setError('Ο winner είναι μέσα στα επιλεγμένα — αφαίρεσέ τον.');
      return;
    }
    const ok = window.confirm(
      `ΕΠΙΒΕΒΑΙΩΣΗ\n\nΘα συγχωνευτούν ${ids.length} canonicals σε:\n  ${bulkWinner.display_artist} — ${bulkWinner.display_title}\n\nΌλες οι αναπαραγωγές θα μεταφερθούν, και τα παλιά ονόματα θα μπουν σε μόνιμο blacklist.\n\nΣίγουρα;`
    );
    if (!ok) return;

    setBulkRunning(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/bad-track/erase-and-merge-bulk`,
        { bad_canonical_ids: ids, winner_canonical_id: bulkWinner.id },
        auth()
      );
      setBulkResult(r.data);
      setBulkSelected(new Set());
      setBulkWinner(null);
      setBulkWinnerQuery('');
      setBulkWinnerResults([]);
      loadSummary();
      // Refresh the search results to reflect merged status
      if (query) search();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBulkRunning(false);
    }
  };

  const searchWinners = async (q) => {
    if (!q || q.trim().length < 2) {
      setWinnerSearchResults([]);
      return;
    }
    setWinnerSearching(true);
    try {
      const r = await axios.get(`${API}/api/admin/bad-track/search`, {
        ...auth(),
        params: { q: q.trim(), limit: 30 },
      });
      const list = (r.data.results || []).filter(
        (x) => !x.is_merged && !x.is_blacklisted && (!bad || x.id !== bad.id)
      );
      setWinnerSearchResults(list);
    } catch (e) {
      setWinnerSearchResults([]);
    } finally {
      setWinnerSearching(false);
    }
  };

  const pickBad = async (row) => {
    setBad(row);
    setWinner(null);
    setWinnerSearchResults([]);
    setLastResult(null);
    setLoadingSuggest(true);
    try {
      const r = await axios.get(
        `${API}/api/admin/bad-track/suggest-winner/${row.id}`,
        auth()
      );
      if (r.data.suggestion) {
        setWinner(r.data.suggestion);
      }
    } catch (_e) {
      // suggestion is optional
    } finally {
      setLoadingSuggest(false);
    }
  };

  const erase = async () => {
    if (!bad || !winner) return;
    if (bad.id === winner.id) {
      setError('Το λάθος και το σωστό δεν μπορούν να είναι το ίδιο.');
      return;
    }
    // Guard-rail: warn if winner has FEWER plays than bad (likely user error)
    if (winner.play_count < bad.play_count) {
      const ok2 = window.confirm(
        `⚠️ ΠΡΟΣΟΧΗ\n\nΟ winner που διάλεξες έχει ΛΙΓΟΤΕΡΕΣ αναπαραγωγές από το λάθος:\n  Winner: ${winner.play_count} plays\n  Λάθος: ${bad.play_count} plays\n\nΣυνήθως αυτό σημαίνει ότι έχεις διαλέξει αντίστροφα. Συνέχεια έτσι;`
      );
      if (!ok2) return;
    }
    const ok = window.confirm(
      `ΕΠΙΒΕΒΑΙΩΣΗ\n\nΛάθος (διαγραφή):\n  ${bad.display_artist} — ${bad.display_title}\n\nΣωστό (winner):\n  ${winner.display_artist} — ${winner.display_title}\n\nΌλες οι ${bad.play_count} αναπαραγωγές θα μεταφερθούν στον winner. Στο μέλλον το λάθος δεν θα ξαναεμφανιστεί. Σίγουρα;`
    );
    if (!ok) return;

    setErasing(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/admin/bad-track/erase-and-merge`,
        { bad_canonical_id: bad.id, winner_canonical_id: winner.id },
        auth()
      );
      setLastResult(r.data);
      setBad(null);
      setWinner(null);
      setResults([]);
      setQuery('');
      loadSummary();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setErasing(false);
    }
  };

  const restoreOne = async (canonicalId) => {
    if (!window.confirm('Επαναφορά αυτού του canonical;')) return;
    try {
      await axios.post(
        `${API}/api/admin/bad-track/restore`,
        { canonical_id: canonicalId },
        auth()
      );
      loadSummary();
      // refresh search if active
      if (query) search();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="space-y-4" data-testid="bad-track-eraser">
      {/* Επικεφαλίδα */}
      <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-gradient-to-r from-rose-50 to-orange-50 dark:from-rose-900/20 dark:to-orange-900/20 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-rose-600 dark:text-rose-300 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-rose-900 dark:text-rose-100">
              🧹 Διαγραφή Λάθος Τραγουδιού
            </h3>
            <p className="text-sm text-rose-800 dark:text-rose-200 mt-1">
              Διαλέγεις ένα <strong>λάθος canonical</strong> (π.χ. "David Blasucci — Gone Gone Gone")
              και έναν <strong>winner</strong>. Όλες οι παλιές αναπαραγωγές
              μεταφέρονται, το λάθος εξαφανίζεται από timeline/charts/analytics, και
              μπαίνει σε <strong>μόνιμο blacklist</strong> ώστε να μη ξανα-εμφανιστεί
              ποτέ — ούτε αν το AudD ή κάποιος σταθμός το ξαναστείλει.
              Η ενέργεια είναι αναστρέψιμη.
            </p>
            {summary && (
              <div className="mt-2 flex gap-3 text-xs">
                <span className="px-2 py-0.5 rounded bg-rose-100 dark:bg-rose-900/40 text-rose-700 dark:text-rose-200">
                  Blacklist entries: <strong>{summary.blacklist_entries}</strong>
                </span>
                <span className="px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-200">
                  Διαγραμμένα canonicals: <strong>{summary.blacklisted_canonicals}</strong>
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Πρόοδος Συγκλίσεων */}
      {progress && (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-white dark:bg-gray-800/40 p-4" data-testid="bad-track-progress-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center gap-2">
              📊 Πρόοδος Συγκλίσεων
            </h4>
            <button
              onClick={loadSummary}
              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 inline-flex items-center gap-1"
              data-testid="bad-track-progress-refresh"
            >
              <RefreshCw className="w-3 h-3" /> Ανανέωση
            </button>
          </div>

          {/* Metric cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
            <div className="rounded-md p-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
              <div className="text-[10px] uppercase tracking-wide text-indigo-700 dark:text-indigo-300">🪄 Auto-Σύγκλισεις</div>
              <div className="text-lg font-bold text-indigo-900 dark:text-indigo-100 tabular-nums">{progress.auto_convergence.total_hits}</div>
              <div className="text-[10px] text-indigo-600 dark:text-indigo-400">σε {progress.auto_convergence.winners_count} winners</div>
            </div>

            <div className="rounded-md p-2 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
              <div className="text-[10px] uppercase tracking-wide text-rose-700 dark:text-rose-300">🧹 Manual σήμερα</div>
              <div className="text-lg font-bold text-rose-900 dark:text-rose-100 tabular-nums">{progress.manual_erase.today}</div>
              <div className="text-[10px] text-rose-600 dark:text-rose-400">σύνολο: {progress.manual_erase.total}</div>
            </div>

            <div className="rounded-md p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <div className="text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">✅ Ενεργά canonicals</div>
              <div className="text-lg font-bold text-emerald-900 dark:text-emerald-100 tabular-nums">{progress.canonicals.active.toLocaleString('el-GR')}</div>
            </div>

            <div className="rounded-md p-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="text-[10px] uppercase tracking-wide text-gray-700 dark:text-gray-300">🔗 Merged</div>
              <div className="text-lg font-bold text-gray-900 dark:text-gray-100 tabular-nums">{progress.canonicals.merged.toLocaleString('el-GR')}</div>
            </div>

            <div className="rounded-md p-2 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800">
              <div className="text-[10px] uppercase tracking-wide text-orange-700 dark:text-orange-300">🚫 Blacklisted</div>
              <div className="text-lg font-bold text-orange-900 dark:text-orange-100 tabular-nums">{progress.canonicals.blacklisted.toLocaleString('el-GR')}</div>
            </div>

            <div className="rounded-md p-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800">
              <div className="text-[10px] uppercase tracking-wide text-sky-700 dark:text-sky-300">📦 Σύνολο</div>
              <div className="text-lg font-bold text-sky-900 dark:text-sky-100 tabular-nums">
                {(progress.canonicals.active + progress.canonicals.merged + progress.canonicals.blacklisted).toLocaleString('el-GR')}
              </div>
            </div>
          </div>

          {/* Top 5 absorbers */}
          {progress.auto_convergence.top_winners.length > 0 && (
            <div>
              <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                🏆 Top 5 Winners (αυτόματα τραβάνε παραλλαγές)
              </div>
              <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-gray-900/40">
                    <tr className="text-left text-gray-600 dark:text-gray-400">
                      <th className="px-2 py-1">#</th>
                      <th className="px-2 py-1">Artist — Title</th>
                      <th className="px-2 py-1 text-right">Plays</th>
                      <th className="px-2 py-1 text-right">🪄 Hits</th>
                      <th className="px-2 py-1 text-right">Aliases</th>
                      <th className="px-2 py-1 text-right">UIDs</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {progress.auto_convergence.top_winners.map((w, i) => (
                      <tr key={w.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-2 py-1 text-gray-500">{i + 1}</td>
                        <td className="px-2 py-1 font-mono text-gray-800 dark:text-gray-200 max-w-[420px] truncate">
                          {w.display_artist} — {w.display_title}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums">{w.play_count}</td>
                        <td className="px-2 py-1 text-right tabular-nums font-bold text-indigo-700 dark:text-indigo-300">{w.convergence_hits}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-500">{w.aliases_count}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-gray-500">{w.uid_variants_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4" />
          {error}
        </div>
      )}

      {lastResult && (
        <div className="p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 text-sm">
          <div className="flex items-center gap-2 font-semibold mb-1">
            <CheckCircle2 className="w-4 h-4" />
            Ολοκληρώθηκε επιτυχώς
          </div>
          <div className="text-xs space-y-0.5 ml-6">
            <div>Λάθος: <code>{lastResult.bad_display}</code></div>
            <div>Winner: <code>{lastResult.winner_display}</code></div>
            <div>Plays που μεταφέρθηκαν: <strong>{lastResult.plays_moved}</strong></div>
            <div>Blacklist keys που προστέθηκαν: <strong>{lastResult.blacklist_keys_added}</strong></div>
            <div>Νέο play_count του winner: <strong>{lastResult.winner_play_count_after}</strong></div>
          </div>
        </div>
      )}

      {/* Βήμα 1: Αναζήτηση λάθος τραγουδιού */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-4">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
          1️⃣ Βρες το λάθος τραγούδι
        </h4>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && search()}
              placeholder="π.χ. David Blasucci, Gone Gone Gone, ή normalized_key"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-sm"
              data-testid="bad-track-search-input"
            />
          </div>
          <button
            onClick={search}
            disabled={searching}
            className="px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white text-sm font-medium inline-flex items-center gap-1.5"
            data-testid="bad-track-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Αναζήτηση
          </button>
        </div>

        {results.length > 0 && (
          <>
            {/* Bulk action bar */}
            <div className="mt-3 flex flex-wrap items-center gap-2 p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800">
              <Layers className="w-4 h-4 text-indigo-600 dark:text-indigo-300" />
              <span className="text-xs text-indigo-900 dark:text-indigo-100 font-medium">
                Επιλεγμένα: <strong>{bulkSelected.size}</strong>
              </span>
              {bulkSelected.size > 0 && (
                <button
                  onClick={() => setBulkSelected(new Set())}
                  className="text-[10px] text-indigo-700 dark:text-indigo-300 hover:underline"
                  data-testid="bad-track-bulk-clear"
                >
                  εκκαθάριση
                </button>
              )}
              <div className="flex-1" />
              {bulkSelected.size > 0 && (
                bulkWinner ? (
                  <span className="text-xs text-emerald-700 dark:text-emerald-300 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Winner:
                    <strong className="ml-1 truncate max-w-[260px]">
                      {bulkWinner.display_artist} — {bulkWinner.display_title}
                    </strong>
                    <button
                      onClick={() => setBulkWinner(null)}
                      className="ml-1 text-rose-600 hover:underline"
                    >αλλαγή</button>
                  </span>
                ) : (
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Διάλεξε winner παρακάτω ↓
                  </span>
                )
              )}
              {bulkSelected.size > 0 && bulkWinner && (
                <button
                  onClick={runBulkMerge}
                  disabled={bulkRunning}
                  className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white text-xs font-bold inline-flex items-center gap-1.5"
                  data-testid="bad-track-bulk-run"
                >
                  {bulkRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                  Συγχώνευση {bulkSelected.size} → winner
                </button>
              )}
            </div>

            {/* Bulk winner picker (visible only when items are selected and no winner yet) */}
            {bulkSelected.size > 0 && !bulkWinner && (
              <div className="mt-2 p-3 rounded-lg bg-white dark:bg-gray-800/60 border border-emerald-200 dark:border-emerald-800">
                <p className="text-xs font-medium text-gray-800 dark:text-gray-200 mb-2">
                  Διάλεξε σε ποιον winner θα συγχωνευτούν τα {bulkSelected.size} επιλεγμένα:
                </p>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={bulkWinnerQuery}
                    onChange={(e) => {
                      setBulkWinnerQuery(e.target.value);
                      if (e.target.value.trim().length >= 3) searchBulkWinners(e.target.value);
                      else setBulkWinnerResults([]);
                    }}
                    placeholder="π.χ. David Guetta"
                    className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-xs"
                    data-testid="bad-track-bulk-winner-search"
                  />
                  {bulkWinnerSearching && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                </div>
                {bulkWinnerResults.length > 0 && (
                  <div className="mt-2 max-h-44 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                    {bulkWinnerResults.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => { setBulkWinner(r); setBulkWinnerResults([]); }}
                        className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                        data-testid={`bad-track-bulk-winner-pick-${r.id}`}
                      >
                        <span className="font-mono">{r.display_artist} — {r.display_title}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">({r.play_count} plays)</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bulk result */}
            {bulkResult && (
              <div className="mt-2 p-3 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-800 dark:text-emerald-200 border border-emerald-200 dark:border-emerald-800 text-xs">
                <div className="font-semibold flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4" />
                  Bulk συγχώνευση ολοκληρώθηκε
                </div>
                <div className="ml-6 mt-1 space-y-0.5">
                  <div>Επιτυχημένα: <strong>{bulkResult.succeeded}</strong> / Αποτυχημένα: <strong>{bulkResult.failed}</strong></div>
                  <div>Συνολικά plays που μεταφέρθηκαν: <strong>{bulkResult.total_plays_moved}</strong></div>
                  <div>Νέα blacklist keys: <strong>{bulkResult.total_blacklist_keys}</strong></div>
                </div>
              </div>
            )}

          <div className="mt-3 border border-gray-200 dark:border-gray-700 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40 sticky top-0">
                <tr className="text-left text-gray-700 dark:text-gray-300">
                  <th className="px-2 py-1.5 w-8">
                    <input
                      type="checkbox"
                      checked={
                        results.filter((r) => !r.is_blacklisted).length > 0 &&
                        bulkSelected.size === results.filter((r) => !r.is_blacklisted).length
                      }
                      onChange={toggleBulkAll}
                      className="cursor-pointer accent-rose-600"
                      data-testid="bad-track-bulk-select-all"
                    />
                  </th>
                  <th className="px-2 py-1.5">Artist — Title</th>
                  <th className="px-2 py-1.5">Plays</th>
                  <th className="px-2 py-1.5">Κατάσταση</th>
                  <th className="px-2 py-1.5">song_uid</th>
                  <th className="px-2 py-1.5 text-right">Ενέργεια</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {results.map((r) => (
                  <tr
                    key={r.id}
                    className={`${
                      bad?.id === r.id ? 'bg-rose-50 dark:bg-rose-900/20'
                      : bulkSelected.has(r.id) ? 'bg-indigo-50 dark:bg-indigo-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                    data-testid={`bad-track-row-${r.id}`}
                  >
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={bulkSelected.has(r.id)}
                        disabled={r.is_blacklisted}
                        onChange={() => toggleBulkRow(r.id)}
                        className="cursor-pointer accent-rose-600 disabled:opacity-30"
                        data-testid={`bad-track-bulk-check-${r.id}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-gray-800 dark:text-gray-200 max-w-[420px]">
                      <div className="truncate">{r.display_artist} — {r.display_title}</div>
                      {r.merged_into_info && (
                        <div className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                          → συγχωνεύτηκε σε:{' '}
                          <span className="text-emerald-600 dark:text-emerald-400">
                            {r.merged_into_info.display_artist} — {r.merged_into_info.display_title}
                          </span>{' '}
                          ({r.merged_into_info.play_count} plays)
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5 tabular-nums">
                      {r.play_count}
                      {r.is_merged && r.cached_play_count !== r.play_count && (
                        <div className="text-[10px] text-gray-400 dark:text-gray-500">
                          (cached: {r.cached_play_count})
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.is_blacklisted ? (
                        <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200 text-[10px]">
                          διαγραμμένο
                        </span>
                      ) : r.is_merged ? (
                        <span
                          className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-[10px] cursor-help"
                          title="Έχει ήδη συγχωνευτεί σε άλλο canonical. Μπορείς να το ξαναδρομολογήσεις σε διαφορετικό winner."
                        >
                          merged
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 text-[10px]">
                          ενεργό
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-500 dark:text-gray-400">
                      {r.song_uid || '—'}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {r.is_blacklisted ? (
                        <button
                          onClick={() => restoreOne(r.id)}
                          className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-[11px] inline-flex items-center gap-1"
                          data-testid={`bad-track-restore-${r.id}`}
                        >
                          <Undo2 className="w-3 h-3" /> Επαναφορά
                        </button>
                      ) : (
                        <button
                          onClick={() => pickBad(r)}
                          className="px-2 py-1 rounded bg-rose-100 hover:bg-rose-200 dark:bg-rose-900/40 dark:hover:bg-rose-900/60 text-rose-700 dark:text-rose-200 text-[11px] inline-flex items-center gap-1"
                          data-testid={`bad-track-pick-${r.id}`}
                          title={r.is_merged ? 'Έχει ήδη συγχωνευτεί — επιλογή για ξαναδρομολόγηση σε διαφορετικό winner' : 'Σημείωσε ως λάθος'}
                        >
                          <Trash2 className="w-3 h-3" />{' '}
                          {r.is_merged ? 'Αλλαγή winner' : 'Λάθος αυτό'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Βήμα 2: Επιλογή Winner */}
      {bad && (
        <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-white dark:bg-gray-800/40 p-4">
          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
            2️⃣ Διάλεξε τον σωστό (winner)
          </h4>

          <div className="rounded p-2 mb-3 text-xs bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
            <div className="text-rose-700 dark:text-rose-200">
              <strong>Λάθος που θα διαγραφεί:</strong>{' '}
              {bad.display_artist} — {bad.display_title} ({bad.play_count} plays)
            </div>
          </div>

          {loadingSuggest && (
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 mb-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Αναζητώ τον winner αυτόματα...
            </div>
          )}

          {winner ? (
            <div className="rounded p-2 mb-2 text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <div className="text-emerald-700 dark:text-emerald-200 flex items-center justify-between">
                <span>
                  <CheckCircle2 className="w-3 h-3 inline mr-1" />
                  <strong>Winner (επιλεγμένο):</strong>{' '}
                  {winner.display_artist} — {winner.display_title} ({winner.play_count} plays)
                </span>
                <button
                  onClick={() => setWinner(null)}
                  className="text-rose-600 hover:underline"
                >
                  Αλλαγή
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Δεν βρέθηκε αυτόματα. Ψάξε χειροκίνητα:
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={winnerSearchQuery}
                  onChange={(e) => {
                    setWinnerSearchQuery(e.target.value);
                    if (e.target.value.trim().length >= 3) {
                      searchWinners(e.target.value);
                    } else {
                      setWinnerSearchResults([]);
                    }
                  }}
                  placeholder="π.χ. David Guetta"
                  className="flex-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-xs"
                  data-testid="bad-track-winner-search"
                />
                {winnerSearching && <Loader2 className="w-4 h-4 animate-spin text-gray-400 mt-2" />}
              </div>
              {winnerSearchResults.length > 0 && (
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded">
                  {winnerSearchResults.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setWinner(r)}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-emerald-50 dark:hover:bg-emerald-900/20 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                      data-testid={`bad-track-winner-pick-${r.id}`}
                    >
                      <span className="font-mono">{r.display_artist} — {r.display_title}</span>
                      <span className="text-gray-500 dark:text-gray-400 ml-2">({r.play_count} plays)</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Βήμα 3: Apply */}
          {winner && bad.id !== winner.id && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2 text-sm">
                3️⃣ Επιβεβαίωση
              </h4>
              <div className="text-xs flex items-center gap-2 mb-3 text-gray-700 dark:text-gray-300">
                <code className="text-rose-600">{bad.display_artist} — {bad.display_title}</code>
                <ArrowRight className="w-4 h-4 text-gray-500" />
                <code className="text-emerald-600">{winner.display_artist} — {winner.display_title}</code>
              </div>
              <button
                onClick={erase}
                disabled={erasing}
                className="px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400 text-white text-sm font-bold inline-flex items-center gap-2"
                data-testid="bad-track-confirm-erase"
              >
                {erasing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Διαγραφή & Συγχώνευση
              </button>
              <button
                onClick={() => { setBad(null); setWinner(null); }}
                className="ml-2 px-3 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm"
                data-testid="bad-track-cancel"
              >
                Άκυρο
              </button>
            </div>
          )}
        </div>
      )}

      {/* Πρόσφατες διαγραφές */}
      {summary?.recent && summary.recent.length > 0 && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              📜 Πρόσφατες διαγραφές
            </h4>
            <button
              onClick={loadSummary}
              className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 inline-flex items-center gap-1"
              data-testid="bad-track-refresh-summary"
            >
              <RefreshCw className="w-3 h-3" /> Ανανέωση
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr className="text-left text-gray-700 dark:text-gray-300">
                  <th className="px-2 py-1">Λάθος</th>
                  <th className="px-2 py-1">→ Winner</th>
                  <th className="px-2 py-1">Πότε</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {summary.recent.map((r, i) => (
                  <tr key={i} className="text-gray-700 dark:text-gray-300">
                    <td className="px-2 py-1 font-mono">
                      {r.display_artist} — {r.display_title}
                    </td>
                    <td className="px-2 py-1 font-mono text-emerald-700 dark:text-emerald-300">
                      {r.winner_display_artist} — {r.winner_display_title}
                    </td>
                    <td className="px-2 py-1 text-[10px] text-gray-500 dark:text-gray-400">
                      {r.blacklisted_at ? new Date(r.blacklisted_at).toLocaleString('el-GR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BadTrackEraser;
